import { useEffect, useMemo, useRef, useState } from "react";
import {
  FileText, Eye, Download, X, Filter, Pill, Save, Plus, Trash2, Upload,
  Stethoscope, Image as ImageIcon, BadgeCheck, ClipboardList, MessageSquarePlus, MapPin,
} from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "@/lib/toast";
import ApptNotesModal from "@/components/portal/ApptNotesModal";

const STATUS_OPTS = ["upcoming", "completed", "pending", "missed"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const blankMed = () => ({ form: "", drug: "", dose: "", amount: "", frequency: "", duration: "" });

export default function DoctorMyAppointments() {
  const [appts, setAppts] = useState([]);
  const [busy, setBusy] = useState(true);
  const [fStatus, setFStatus] = useState("");
  const [fDate, setFDate] = useState("");
  const [active, setActive] = useState(null); // active appointment id (drill-down)
  const [rxFor, setRxFor] = useState(null);   // open prescription modal for this id
  const [notesFor, setNotesFor] = useState(null); // open private notes modal
  const [profile, setProfile] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/appointments");
      setAppts(r.data.appointments || []);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const loadProfile = async () => {
    try { const r = await api.get("/doctor/me/profile"); setProfile(r.data.profile); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); loadProfile(); }, []);

  const filtered = useMemo(() => appts.filter(a => {
    if (fStatus && a.status !== fStatus) return false;
    if (fDate && a.date !== fDate) return false;
    return true;
  }).filter(a => a.kind !== "peer"), [appts, fStatus, fDate]);

  if (active) {
    const a = appts.find(x => x.id === active);
    return (
      <>
        <DoctorApptDrill
          appt={a}
          onBack={() => setActive(null)}
          onOpenRx={() => setRxFor(active)}
          onChange={load}
        />
        {rxFor && (
          <PrescriptionModal
            appt={appts.find(x => x.id === rxFor)}
            profile={profile}
            onClose={() => setRxFor(null)}
            onIssued={() => { setRxFor(null); load(); }}
          />
        )}
      </>
    );
  }

  const clear = () => { setFStatus(""); setFDate(""); };

  const downloadRx = async (id) => {
    try {
      const res = await api.get(`/appointments/${id}/document?kind=prescription`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const aEl = document.createElement("a"); aEl.href = url; aEl.download = `prescription-${id}.pdf`; aEl.click();
    } catch (e) { toast(formatApiError(e.response?.data?.detail) || "Prescription not available."); }
  };

  return (
    <div data-testid="doc-my-appts">
      {profile && (!profile.signature_id || !profile.seal_id) && (
        <div className="banner warn" style={{ marginBottom: 12 }} data-testid="doc-needs-stamps">
          You haven&apos;t uploaded your <b>signature</b> and/or <b>seal</b> yet. Please upload both in your profile before issuing any prescription.
        </div>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Filter size={16} color="#185FA5" />
          <b style={{ color: "var(--ink)" }}>Filter appointments</b>
          {(fStatus || fDate) && (
            <button className="btn btn-ghost btn-sm" onClick={clear} data-testid="doc-appts-filter-clear">
              <X size={12} /> Clear
            </button>
          )}
        </div>
        <div className="appts-filter-row">
          <select className="in" value={fStatus} onChange={e => setFStatus(e.target.value)} data-testid="doc-appts-filter-status">
            <option value="">All statuses</option>
            {STATUS_OPTS.map(s => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
          </select>
          <input className="in" type="date" value={fDate} onChange={e => setFDate(e.target.value)} data-testid="doc-appts-filter-date" />
        </div>
      </div>

      {busy && <p className="lead">Loading…</p>}
      {!busy && filtered.length === 0 && (
        <div className="card"><p className="lead" style={{ margin: 0 }}>No appointments to show.</p></div>
      )}
      {!busy && filtered.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table className="appts-table" data-testid="doc-appts-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Patient</th>
                <th>Pt. ID</th>
                <th>Status</th>
                <th>Diagnosis</th>
                <th>Prescription</th>
                <th>Documents</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id} data-testid={`doc-appts-row-${a.id}`}>
                  <td>
                    <div className="appts-date">
                      <b>{fmtDate(a.date)}</b>
                      <small>{a.time}</small>
                    </div>
                  </td>
                  <td>
                    <button className="link-btn" onClick={() => setActive(a.id)} data-testid={`doc-appts-open-${a.id}`}>
                      {a.patient_name || "—"}
                    </button>
                    {(a.patient_age || a.patient_sex) && (
                      <small style={{ color: "var(--slate)", display: "block" }}>
                        {[a.patient_age && `${a.patient_age}y`, a.patient_sex].filter(Boolean).join(" · ")}
                      </small>
                    )}
                    {a.booked_for_relationship && (
                      <small className="forwho-chip" data-testid={`doc-appts-forwho-${a.id}`}>
                        {a.booked_for_relationship} of booker
                      </small>
                    )}
                    {a.consult_type === "Clinic visit" && (
                      <small className="clinic-visit-chip" data-testid={`doc-appts-clinic-${a.id}`}>
                        <MapPin size={11} /> Clinic visit{a.clinic_name ? ` · ${a.clinic_name}` : ""}
                      </small>
                    )}
                  </td>
                  <td><span className="pid-chip">{a.patient_code || "—"}</span></td>
                  <td><span className={`badge ${a.status}`}>{a.status}</span></td>
                  <td style={{ maxWidth: 220 }}>
                    {a.diagnosis ? a.diagnosis : <span style={{ color: "var(--slate)" }}>—</span>}
                  </td>
                  <td>
                    {a.has_prescription
                      ? <button className="btn btn-ghost btn-sm" onClick={() => downloadRx(a.id)} data-testid={`doc-rx-dl-${a.id}`}><Download size={13} /> PDF</button>
                      : <span style={{ color: "var(--slate)" }}>—</span>}
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => setActive(a.id)} data-testid={`doc-docs-open-${a.id}`}>
                      <FileText size={13} /> {(a.files || []).length}
                    </button>
                  </td>
                  <td>
                    <button
                      className="btn btn-ghost btn-sm appts-msg-btn"
                      onClick={() => setNotesFor(a.id)}
                      data-testid={`doc-appts-msg-${a.id}`}
                      title="View / reply to this patient's private health notes"
                    >
                      <MessageSquarePlus size={13} /> Message
                      {a.note_unread > 0 && (
                        <span className="appts-msg-badge" data-testid={`doc-appts-msg-badge-${a.id}`}>{a.note_unread}</span>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rxFor && (
        <PrescriptionModal
          appt={appts.find(x => x.id === rxFor)}
          profile={profile}
          onClose={() => setRxFor(null)}
          onIssued={() => { setRxFor(null); load(); }}
        />
      )}

      {notesFor && (
        <ApptNotesModal
          appt={appts.find(x => x.id === notesFor)}
          role="doctor"
          accent="#1D9E75"
          onClose={() => { setNotesFor(null); load(); }}
        />
      )}
    </div>
  );
}

function DoctorApptDrill({ appt, onBack, onOpenRx, onChange }) {
  const [diag, setDiag] = useState(appt?.diagnosis || "");
  const [saving, setSaving] = useState(false);
  if (!appt) return <p className="lead">Loading…</p>;

  const completed = appt.status === "completed";
  const saveDiag = async () => {
    if (!diag.trim()) { toast("Please enter a diagnosis."); return; }
    setSaving(true);
    try {
      await api.post(`/appointments/${appt.id}/diagnosis`, { diagnosis: diag.trim() });
      toast("Diagnosis saved ✓"); onChange();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const viewDoc = (fid) => window.open(`${API}/files/${fid}`, "_blank");
  const downloadDoc = async (fid, fname) => {
    try {
      const { data } = await api.get(`/files/${fid}/link?download=true`);
      if (data?.url) {
        const aEl = document.createElement("a"); aEl.href = data.url; aEl.download = fname; aEl.click();
        return;
      }
      const res = await api.get(`/files/${fid}?download=true`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const aEl = document.createElement("a"); aEl.href = url; aEl.download = fname; aEl.click();
    } catch (e) {
      toast(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="doc-appt-drill">
      <button className="back" onClick={onBack} data-testid="doc-drill-back">← Back to list</button>

      <div className="idcard">
        <div>
          <div className="idname">{appt.patient_name}</div>
          <div className="idspec">
            {appt.patient_code || "—"}
            {(appt.patient_age || appt.patient_sex) && (
              <> · {[appt.patient_age && `Age ${appt.patient_age}`, appt.patient_sex].filter(Boolean).join(" · ")}</>
            )}
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <span className={`badge ${appt.status}`}>{appt.status}</span>
            <span style={{ color: "#AFC6DE", fontSize: 13 }}>{appt.date} · {appt.time}</span>
            <span style={{ color: "#AFC6DE", fontSize: 13 }}>{appt.specialty}</span>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>
          <ClipboardList size={16} style={{ verticalAlign: "-3px" }} /> Patient&apos;s pre-consult notes
        </h4>
        <p className="lead" style={{ whiteSpace: "pre-wrap", margin: 0 }} data-testid="doc-drill-pnotes">
          {appt.patient_notes || <span style={{ color: "var(--slate)" }}>The patient has not added any notes for this consultation.</span>}
        </p>
        {appt.symptom && (
          <p className="lead" style={{ marginTop: 8, marginBottom: 0 }}>
            <small style={{ color: "var(--slate)" }}>Chief complaint at booking:</small> {appt.symptom}
          </p>
        )}
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Diagnosis</h4>
        <textarea className="in" rows={2} value={diag} onChange={e => setDiag(e.target.value)} disabled={completed}
                  placeholder="e.g. Essential hypertension (I10) — Stage 1" data-testid="doc-drill-diag" />
        {!completed && (
          <button className="btn btn-ink btn-sm" style={{ marginTop: 8 }} onClick={saveDiag} disabled={saving}
                  data-testid="doc-drill-diag-save">
            <Save size={13} /> {saving ? "Saving…" : "Save diagnosis"}
          </button>
        )}
        {completed && <p className="prof-hint" style={{ marginTop: 6 }}>This consultation is completed — diagnosis is locked.</p>}
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Patient documents</h4>
        {(appt.files || []).length === 0 && <p className="lead" style={{ margin: 0 }}>No documents uploaded by the patient.</p>}
        {(appt.files || []).map((f, i) => (
          <div className="filechip" key={f.file_id || i}>
            <FileText size={15} /> {f.filename}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => viewDoc(f.file_id)} data-testid={`doc-drill-view-${i}`}>
                <Eye size={13} /> View
              </button>
              {completed && (
                <button className="btn btn-ghost btn-sm" onClick={() => downloadDoc(f.file_id, f.filename)} data-testid={`doc-drill-dl-${i}`}>
                  <Download size={13} />
                </button>
              )}
            </div>
          </div>
        ))}
        {!completed && <p className="prof-hint" style={{ marginTop: 8 }}>Downloads are enabled once the consultation is marked completed.</p>}
      </div>

      <div className="card">
        <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>
          <Pill size={16} style={{ verticalAlign: "-3px" }} /> Prescription
        </h4>
        {appt.has_prescription
          ? <p className="lead" style={{ margin: 0 }}>A prescription has been issued for this consultation. The patient can download the PDF from their portal.</p>
          : <p className="lead" style={{ margin: 0 }}>No prescription issued yet.</p>}
        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
          {appt.has_prescription && (
            <a className="btn btn-ghost btn-sm" href="#" onClick={(e) => { e.preventDefault(); api.get(`/appointments/${appt.id}/document?kind=prescription`, { responseType: "blob" }).then(res => { const url = URL.createObjectURL(res.data); const aEl = document.createElement("a"); aEl.href = url; aEl.download = `prescription-${appt.id}.pdf`; aEl.click(); }).catch(err => toast(formatApiError(err.response?.data?.detail))); }} data-testid="doc-drill-rx-dl"><Download size={13} /> Download PDF</a>
          )}
          {!completed && (
            <button className="btn btn-green" onClick={onOpenRx} data-testid="doc-drill-issue-rx">
              <Pill size={14} /> {appt.has_prescription ? "Re-issue prescription" : "Issue prescription"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PrescriptionModal({ appt, profile, onClose, onIssued }) {
  const [doctorNotes, setDoctorNotes] = useState(appt?.doctor_notes || appt?.notes || "");
  const [pastHistory, setPastHistory] = useState(appt?.past_history || "");
  const [allergyHistory, setAllergyHistory] = useState(appt?.allergy_history || "");
  const initialMeds = (appt?.medicines && appt.medicines.length > 0) ? appt.medicines : [blankMed()];
  const [meds, setMeds] = useState(initialMeds);
  const [busy, setBusy] = useState(false);

  if (!appt) return null;
  const diag = appt.diagnosis || "";
  const noDiag = !diag.trim();
  const noStamps = !profile?.signature_id || !profile?.seal_id;

  const addMed = () => {
    if (meds.length >= 10) { toast("You can prescribe up to 10 medicines."); return; }
    setMeds([...meds, blankMed()]);
  };
  const removeMed = (i) => setMeds(meds.filter((_, idx) => idx !== i));
  const updateMed = (i, k, v) => setMeds(meds.map((m, idx) => idx === i ? { ...m, [k]: v } : m));

  const submit = async () => {
    if (noDiag) { toast("Add a diagnosis first."); return; }
    if (noStamps) { toast("Upload your signature and seal images in your profile first."); return; }
    const valid = meds.filter(m => (m.drug || "").trim());
    if (valid.length === 0) { toast("Please add at least one medicine."); return; }
    setBusy(true);
    try {
      await api.post(`/appointments/${appt.id}/prescription`, {
        doctor_notes: doctorNotes, past_history: pastHistory, allergy_history: allergyHistory,
        medicines: valid, complete: true,
      });
      toast("Prescription issued ✓ — consultation marked completed.");
      onIssued();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal rx-modal" onClick={e => e.stopPropagation()} data-testid="rx-modal" style={{ maxWidth: 900 }}>
        <button className="m-close" onClick={onClose}>✕</button>
        <h3 className="h" style={{ fontSize: 22, marginTop: 0 }}>
          <Pill size={20} style={{ verticalAlign: "-3px" }} /> Issue prescription
        </h3>

        {noStamps && (
          <div className="banner warn" data-testid="rx-no-stamps">
            Upload your <b>signature</b> and <b>seal</b> images in your profile before issuing a prescription.
          </div>
        )}
        {noDiag && (
          <div className="banner warn" data-testid="rx-no-diag">
            A diagnosis is required before issuing a prescription. Please save the diagnosis on the appointment first.
          </div>
        )}

        <div className="rx-meta">
          <div className="rx-meta-cell"><small>Patient</small><b>{appt.patient_name}</b></div>
          <div className="rx-meta-cell"><small>Age / Sex</small><b>{[appt.patient_age, appt.patient_sex].filter(Boolean).join(" / ") || "—"}</b></div>
          <div className="rx-meta-cell"><small>Date / Time</small><b>{appt.date} · {appt.time}</b></div>
          <div className="rx-meta-cell"><small>Diagnosis</small><b style={{ color: noDiag ? "var(--lred)" : "var(--ink)" }}>{diag || "—"}</b></div>
        </div>

        <div className="rx-grid">
          <div className="field">
            <label>Doctor&apos;s notes (chief complaints / history)</label>
            <textarea className="in" rows={3} value={doctorNotes} onChange={e => setDoctorNotes(e.target.value)} data-testid="rx-doc-notes" />
          </div>
          <div className="field">
            <label>Past history</label>
            <textarea className="in" rows={2} value={pastHistory} onChange={e => setPastHistory(e.target.value)} data-testid="rx-past" />
          </div>
          <div className="field">
            <label>Allergy history</label>
            <textarea className="in" rows={2} value={allergyHistory} onChange={e => setAllergyHistory(e.target.value)} data-testid="rx-allergy" />
          </div>
        </div>

        <h4 className="h" style={{ fontSize: 15, margin: "10px 0 6px" }}>Medicines <span style={{ color: "var(--slate)", fontWeight: 400, fontSize: 12 }}>(up to 10)</span></h4>
        <div className="rx-table-wrap" data-testid="rx-meds">
          <table className="rx-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Form</th>
                <th>Generic drug</th>
                <th>Dose</th>
                <th>Amount</th>
                <th>Frequency</th>
                <th>Duration</th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {meds.map((m, i) => (
                <tr key={i} data-testid={`rx-row-${i}`}>
                  <td className="rx-sl">{i + 1}</td>
                  <td><input className="in rxin" value={m.form} onChange={e => updateMed(i, "form", e.target.value)} placeholder="Tablet / Syrup" data-testid={`rx-form-${i}`} /></td>
                  <td><input className="in rxin" value={m.drug} onChange={e => updateMed(i, "drug", e.target.value)} placeholder="Paracetamol" data-testid={`rx-drug-${i}`} /></td>
                  <td><input className="in rxin" value={m.dose} onChange={e => updateMed(i, "dose", e.target.value)} placeholder="500 mg" data-testid={`rx-dose-${i}`} /></td>
                  <td><input className="in rxin" value={m.amount} onChange={e => updateMed(i, "amount", e.target.value)} placeholder="1 tab / 5 ml" data-testid={`rx-amount-${i}`} /></td>
                  <td><input className="in rxin" value={m.frequency} onChange={e => updateMed(i, "frequency", e.target.value)} placeholder="1-0-1" data-testid={`rx-freq-${i}`} /></td>
                  <td><input className="in rxin" value={m.duration} onChange={e => updateMed(i, "duration", e.target.value)} placeholder="5 days" data-testid={`rx-dur-${i}`} /></td>
                  <td>
                    {meds.length > 1 && (
                      <button type="button" className="rx-row-del" title="Remove" onClick={() => removeMed(i)} data-testid={`rx-del-${i}`}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={addMed} disabled={meds.length >= 10} data-testid="rx-add-row">
          <Plus size={13} /> Add another medicine
        </button>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-green" onClick={submit} disabled={busy || noDiag || noStamps} data-testid="rx-submit">
            <BadgeCheck size={14} /> {busy ? "Issuing…" : "Issue prescription & complete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Stamps uploader — used in the doctor profile view */
export function StampUploader({ profile, onUpdate }) {
  const sigRef = useRef(null);
  const sealRef = useRef(null);
  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/doctor/${kind}`, fd);
      toast(`${kind === "signature" ? "Signature" : "Seal"} uploaded ✓`);
      onUpdate?.();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="card stamps-card" data-testid="stamps-card">
      <h4 className="h" style={{ fontSize: 16, marginTop: 0 }}>Signature &amp; seal <span className="priv-tag">Required for prescriptions</span></h4>
      <p className="lead" style={{ marginTop: 0, fontSize: 13 }}>
        These images appear on every prescription you issue. Use a transparent PNG for the cleanest result.
      </p>
      <div className="stamps-grid">
        <div className="stamp-box" data-testid="stamp-signature">
          <small>Signature</small>
          <div className="stamp-img-wrap">
            {profile?.signature_id
              ? <img alt="Signature" src={`${API}/files/${profile.signature_id}`} />
              : <span style={{ color: "var(--slate)" }}>Not uploaded</span>}
          </div>
          <input ref={sigRef} type="file" accept="image/*" hidden onChange={e => upload("signature", e.target.files[0])} data-testid="stamp-sig-input" />
          <button className="btn btn-ghost btn-sm" onClick={() => sigRef.current?.click()} data-testid="stamp-sig-btn">
            <Upload size={13} /> {profile?.signature_id ? "Replace" : "Upload"}
          </button>
        </div>
        <div className="stamp-box" data-testid="stamp-seal">
          <small>Seal</small>
          <div className="stamp-img-wrap">
            {profile?.seal_id
              ? <img alt="Seal" src={`${API}/files/${profile.seal_id}`} />
              : <span style={{ color: "var(--slate)" }}>Not uploaded</span>}
          </div>
          <input ref={sealRef} type="file" accept="image/*" hidden onChange={e => upload("seal", e.target.files[0])} data-testid="stamp-seal-input" />
          <button className="btn btn-ghost btn-sm" onClick={() => sealRef.current?.click()} data-testid="stamp-seal-btn">
            <Upload size={13} /> {profile?.seal_id ? "Replace" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
